import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, Menu, MenuItem, MenuTrigger, Text } from '@react-spectrum/s2';
import type { Selection } from '@react-types/shared';
import { Filter } from '../icons/s2';

export interface TableFilterOption<K extends string> {
  id: K;
  label: string;
}

export interface TableFilterMenuProps<K extends string> {
  /** Accessible name of the filter, e.g. "Filter users by access". */
  'aria-label': string;
  options: TableFilterOption<K>[];
  value: K;
  /** The option that means "no filter". While it's selected the trigger is a
   *  bare funnel; any other selection shows its label next to the funnel so
   *  the table never looks unfiltered when it isn't. */
  defaultValue: K;
  onChange: (value: K) => void;
}

/**
 * Single-select table filter: a quiet funnel ActionButton opening an S2 Menu
 * (same trigger pattern as the analytics channel filter).
 */
export function TableFilterMenu<K extends string>({
  options,
  value,
  defaultValue,
  onChange,
  ...props
}: TableFilterMenuProps<K>): React.ReactElement {
  const { t } = useTranslation();
  const selected = options.find((o) => o.id === value);
  const isFiltered = value !== defaultValue;

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') return;
    const [first] = Array.from(keys);
    if (first !== undefined) onChange(String(first) as K);
  };

  return (
    <MenuTrigger align="start">
      <ActionButton
        isQuiet
        aria-label={t('components.tableFilterMenu.triggerAria', {
          label: props['aria-label'],
          value: selected?.label ?? '',
        })}
      >
        <Filter size="S" />
        {isFiltered && selected && <Text>{selected.label}</Text>}
      </ActionButton>
      <Menu
        aria-label={props['aria-label']}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={new Set([value])}
        onSelectionChange={handleSelectionChange}
      >
        {options.map((o) => (
          <MenuItem key={o.id} id={o.id} textValue={o.label}>
            <Text>{o.label}</Text>
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}
